import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldDto, SegmentDto } from '@carbonwebmail/contracts'
import { MAX_CHOICE_LENGTH, MAX_CHOICES } from '@carbonwebmail/domain'
import { Link } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { count } from '../../ui/format.js'
import { PillButton } from '../../ui/PillButton.js'
import { Table, Td, Tr } from '../../ui/Table.js'
import type { Mapping, ParsedFile } from './useImport.js'
import { useImport } from './useImport.js'

/**
 * Importing contacts from a CSV.
 *
 * Five steps, because each one is a decision someone can get wrong and a place
 * they should be able to go back from: choose the file, say which column is
 * which, look at what that produced, watch it run, read what happened.
 *
 * The preview is the step that earns its place. Mapping the wrong column is the
 * commonest mistake with a CSV, and it is invisible until you see three real
 * rows rendered as contacts — at which point "these are company names, not
 * people" takes a second rather than an afternoon and a database cleanup.
 */
export function ImportPage({ client }: { readonly client: ApiClient }) {
  const { state, declared, choose, setMapping, goto, start, into, setInto, connectable } =
    useImport({ client })

  return (
    <>
      {/* The board above carries the heading; this is what the tab is for. */}
      <p className="mb-5 text-[13px] text-t2">
        A CSV with a header row. The email column is the only one required.
      </p>

      {/*
        Asked before the file is chosen, because it decides what the import
        does rather than how it reads — and somebody who finds out afterwards
        that everyone landed in no segment has to do it again.
      */}
      <IntoSegment onChange={setInto} segments={connectable} value={into} />

      <Steps current={state.step} />

      <div className="mt-5 rounded-card border border-line bg-surface p-6">
        {state.step === 'upload' ? (
          <Upload problem={state.problem} onChoose={choose} />
        ) : state.step === 'map' ? (
          <MapColumns
            file={state.file}
            mapping={state.mapping}
            declared={declared}
            onChange={setMapping}
            onBack={() => goto('upload')}
            onNext={() => goto('preview')}
          />
        ) : state.step === 'preview' ? (
          <Preview
            file={state.file}
            mapping={state.mapping}
            onBack={() => goto('map')}
            onStart={() => void start(state.file, state.mapping)}
          />
        ) : state.step === 'importing' ? (
          <Importing sent={state.progress.sent} total={state.progress.total} />
        ) : (
          <Done result={state.result} file={state.file} onAgain={() => goto('upload')} />
        )}
      </div>
    </>
  )
}

/**
 * Which segment everyone this adds is connected to.
 *
 * **Chosen here, not only carried in from a link.** A segment's own menu still
 * arrives with one picked, and that is what this opens on; but the board and
 * the sidebar lead here with none, and until 2026-09-19 there was no way to say
 * which — the import ran and connected nobody to anything.
 *
 * "No segment" is a real answer and the default, because importing a list
 * without filing it anywhere is the ordinary case. Only the rows actually
 * inserted are connected: an address already on the list keeps the segments it
 * has, for the same reason it keeps its subscription state.
 *
 * Nothing is offered when no segment exists yet — a picker with one empty
 * option is a control that asks a question with no answers.
 */
function IntoSegment({
  value,
  segments,
  onChange,
}: {
  readonly value: string | undefined
  readonly segments: readonly SegmentDto[]
  readonly onChange: (segmentId: string | undefined) => void
}) {
  if (segments.length === 0) return null

  return (
    <label className="mb-5 flex flex-wrap items-center gap-3 rounded-card border border-line bg-surface-2 p-3">
      <span className="font-medium text-[13px] text-t2">Put these contacts into</span>
      <select
        className="rounded-field border border-line bg-surface px-3 py-1.5 text-[13px] text-t1"
        onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}
        value={value ?? ''}
      >
        <option value="">No segment</option>
        {segments.map((segment) => (
          <option key={segment.segmentId} value={segment.segmentId}>
            {segment.name}
          </option>
        ))}
      </select>
      {value === undefined ? null : (
        <span className="text-[12px] text-t3">
          Addresses already on your list keep the segments they have.
        </span>
      )}
    </label>
  )
}

const STEPS = ['upload', 'map', 'preview', 'importing', 'done'] as const
const STEP_LABELS = ['Upload', 'Map', 'Preview', 'Import', 'Result']

function Steps({ current }: { readonly current: (typeof STEPS)[number] }) {
  const index = STEPS.indexOf(current)

  return (
    <ol className="flex flex-wrap gap-2">
      {STEP_LABELS.map((label, position) => {
        const done = position <= index
        return (
          <li
            key={label}
            className={`flex items-center gap-2 rounded-pill border px-3 py-1.5 text-[12px] ${
              position === index
                ? 'border-brand bg-brand-soft font-semibold text-brand-strong'
                : 'border-line bg-surface text-t3'
            }`}
          >
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-pill font-num text-[11px] ${
                done ? 'bg-brand text-brand-contrast' : 'bg-line-2 text-t3'
              }`}
            >
              {position + 1}
            </span>
            {label}
          </li>
        )
      })}
    </ol>
  )
}

function Upload({
  problem,
  onChoose,
}: {
  readonly problem: string | undefined
  readonly onChoose: (file: File) => void
}) {
  return (
    <div className="text-center">
      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <h2 className="mt-2 font-bold text-[15px] text-t1">Choose a CSV</h2>
      <p className="mx-auto mt-2 mb-5 max-w-md text-[13px] text-t2 leading-relaxed">
        The first row must be a header. Nothing is sent until you have seen the preview.
      </p>

      {/*
        A plain file input rather than a drag-and-drop zone. Drop targets are
        the part of an upload that most often works for the person who built it
        and nobody else — keyboard, screen reader, and every mobile browser get
        this one for free.
      */}
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast">
        Choose file
        <input
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file !== undefined) onChoose(file)
          }}
        />
      </label>
    </div>
  )
}

function MapColumns({
  file,
  mapping,
  declared,
  onChange,
  onBack,
  onNext,
}: {
  readonly file: ParsedFile
  readonly mapping: Mapping
  readonly declared: readonly ContactFieldDto[]
  readonly onChange: (mapping: Mapping) => void
  readonly onBack: () => void
  readonly onNext: () => void
}) {
  return (
    <div>
      <h2 className="font-bold text-[15px] text-t1">Map columns</h2>
      <p className="mt-1 mb-5 text-[13px] text-t2">
        <span className="font-mono">{file.name}</span> · {count(file.rows.length)} rows ·{' '}
        {count(file.headers.length)} columns
      </p>

      <Column
        id="email-column"
        label="Email"
        hint="Required. Rows without a valid address are reported, not imported."
        headers={file.headers}
        value={mapping.email}
        allowNone={false}
        onChange={(value) => onChange({ ...mapping, email: value })}
      />
      <Column
        id="name-column"
        label="Full name"
        hint="Optional. Left empty if you do not map it."
        headers={file.headers}
        value={mapping.name}
        allowNone
        onChange={(value) => onChange({ ...mapping, name: value })}
      />

      {/*
        The fields this organisation declared for itself. Offered only when
        there are some: a "Your fields" heading over nothing would look like a
        feature that failed to load rather than one nobody has used yet.
      */}
      {declared.length === 0 ? null : (
        <div className="mt-6 border-line border-t pt-5">
          <h3 className="font-semibold text-[13px] text-t1">Your fields</h3>
          <p className="mt-1 mb-1 text-[12px] text-t3">
            Columns whose heading matched a field are already chosen. Anything left unmapped is
            simply not imported.
          </p>
          {declared.map((field) => (
            <Column
              key={field.key}
              id={`custom-${field.key}`}
              label={field.label}
              hint={`Stored as {{${field.key}}} — usable in a message and in a segment rule.`}
              headers={file.headers}
              value={mapping.custom[field.key] ?? null}
              allowNone
              onChange={(value) =>
                onChange({ ...mapping, custom: { ...mapping.custom, [field.key]: value } })
              }
            />
          ))}
        </div>
      )}

      <div className="mt-6 flex gap-2">
        <PillButton type="button" variant="secondary" onClick={onBack}>
          Back
        </PillButton>
        <PillButton
          type="button"
          variant="primary"
          disabled={mapping.email === null}
          onClick={onNext}
        >
          Preview rows
        </PillButton>
      </div>
    </div>
  )
}

function Column({
  id,
  label,
  hint,
  headers,
  value,
  allowNone,
  onChange,
}: {
  readonly id: string
  readonly label: string
  readonly hint: string
  readonly headers: readonly string[]
  readonly value: number | null
  readonly allowNone: boolean
  readonly onChange: (value: number | null) => void
}) {
  return (
    <div className="mt-4 first:mt-0">
      <label className="mb-1.5 block font-semibold text-[12px] text-t2" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        value={value === null ? '' : String(value)}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
        className="block w-full max-w-sm rounded-field border border-line bg-surface px-3 py-2.5 text-[14px] text-t1"
      >
        <option value="">{allowNone ? 'Do not import' : 'Choose a column'}</option>
        {headers.map((header, index) => (
          <option key={header} value={index}>
            {header}
          </option>
        ))}
      </select>
      <p className="mt-1.5 text-[12px] text-t3">{hint}</p>
    </div>
  )
}

/** Three rows, rendered as contacts rather than as columns of a file. */
function Preview({
  file,
  mapping,
  onBack,
  onStart,
}: {
  readonly file: ParsedFile
  readonly mapping: Mapping
  readonly onBack: () => void
  readonly onStart: () => void
}) {
  const sample = file.rows.slice(0, 3)

  return (
    <div>
      <h2 className="font-bold text-[15px] text-t1">This is what will be imported</h2>
      <p className="mt-1 mb-4 text-[13px] text-t2">
        The first three rows, as contacts. If these look wrong, the mapping is wrong.
      </p>

      <Table head={['Email', 'Name']} minWidth={520}>
        {sample.map((row) => (
          // Keyed by the whole row. Two rows can carry the same address — that
          // is exactly the case the import reports rather than crashes on — so
          // the address alone is not an identity, and a preview of three rows
          // that never reorder gains nothing from a synthetic one.
          <Tr key={row.join(' ')}>
            <Td mono>{mapping.email === null ? '—' : (row[mapping.email] ?? '')}</Td>
            <Td>{mapping.name === null ? '—' : (row[mapping.name] ?? '')}</Td>
          </Tr>
        ))}
      </Table>

      {/*
        Said before the import, not after. Uploading a list is an assertion
        about consent, and the screen that takes the file is the honest place to
        say what the system will and will not record about it.
      */}
      <p className="mt-4 rounded-card border border-line bg-surface-2 p-4 text-[12px] text-t2 leading-relaxed">
        Importing records{' '}
        <strong className="font-semibold text-t1">where these addresses came from</strong> — this
        file, you, and the time — but not that anyone agreed to hear from you. Consent is yours to
        hold. Addresses already on your list are left exactly as they are, including anyone who has
        unsubscribed.
      </p>

      <div className="mt-5 flex gap-2">
        <PillButton type="button" variant="secondary" onClick={onBack}>
          Back
        </PillButton>
        <PillButton type="button" variant="primary" onClick={onStart}>
          Import {count(file.rows.length)} rows
        </PillButton>
      </div>
    </div>
  )
}

function Importing({ sent, total }: { readonly sent: number; readonly total: number }) {
  const percent = total === 0 ? 0 : Math.round((sent / total) * 100)

  return (
    <div>
      <h2 className="font-bold text-[15px] text-t1">Importing…</h2>
      <p className="mt-1 mb-4 text-[13px] text-t2">
        <span className="font-num tabular-nums">{count(sent)}</span> of{' '}
        <span className="font-num tabular-nums">{count(total)}</span> rows sent. Leaving this page
        stops the import — what has already landed stays.
      </p>

      <div
        className="h-2 w-full overflow-hidden rounded-pill bg-surface-2"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={`h-full rounded-pill bg-brand ${WIDTHS[Math.round(percent / 10)]}`} />
      </div>
    </div>
  )
}

/**
 * Literal classes, in tenths.
 *
 * Tailwind generates classes by scanning source text, so a width assembled at
 * runtime is a class that is not in the stylesheet and renders at zero.
 */
const WIDTHS = [
  'w-0',
  'w-[10%]',
  'w-[20%]',
  'w-[30%]',
  'w-[40%]',
  'w-[50%]',
  'w-[60%]',
  'w-[70%]',
  'w-[80%]',
  'w-[90%]',
  'w-full',
] as const

function Done({
  result,
  file,
  onAgain,
}: {
  readonly result: {
    readonly added: number
    readonly duplicates: number
    readonly rejected: readonly { readonly line: number; readonly reason: string }[]
    readonly rejectedTotal: number
    readonly optionsCreated: readonly {
      readonly column: string
      readonly options: readonly string[]
      readonly noRoom: number
    }[]
  }
  readonly file: string
  readonly onAgain: () => void
}) {
  return (
    <div>
      <h2 className="font-bold text-[15px] text-t1">Import finished</h2>
      <p className="mt-1 mb-5 text-[13px] text-t2">
        <span className="font-mono">{file}</span>
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <Figure label="Added" value={result.added} />
        <Figure label="Already on the list" value={result.duplicates} />
        <Figure label="Rejected" value={result.rejectedTotal} />
      </div>

      {/*
        What the file taught a column. Said rather than done quietly: a dropdown
        gains its options as data arrives, so somebody who declared a column
        with a name and a kind and nothing else can see what it now offers —
        and somebody whose column was already full learns why the board shows
        values it will not offer.
      */}
      {result.optionsCreated.length > 0 ? (
        <div className="mt-5">
          <h3 className="mb-2 font-semibold text-[13px] text-t1">New options</h3>
          <ul className="space-y-1.5">
            {result.optionsCreated.map((created) => (
              <li key={created.column} className="text-[13px] text-t2">
                <span className="font-semibold text-t1">{created.column}</span>
                {created.options.length === 0 ? null : ` — ${created.options.join(', ')}`}
                {created.noRoom === 0 ? null : (
                  <span className="text-t3">
                    {created.options.length === 0 ? ' — ' : '. '}
                    {count(created.noRoom)} more could not be added: a column offers at most{' '}
                    {MAX_CHOICES} options, none longer than {MAX_CHOICE_LENGTH} characters.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {result.rejected.length > 0 ? (
        <div className="mt-5">
          <h3 className="mb-2 font-semibold text-[13px] text-t1">
            {result.rejectedTotal > result.rejected.length
              ? `The first ${count(result.rejected.length)} of ${count(result.rejectedTotal)} problems`
              : 'What was rejected'}
          </h3>
          <ul className="space-y-1.5">
            {result.rejected.map((problem) => (
              <li key={`${problem.line}-${problem.reason}`} className="text-[13px] text-t2">
                <span className="font-num text-t3 tabular-nums">Line {count(problem.line)}</span> —{' '}
                {problem.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-6 flex items-center gap-4">
        <PillButton type="button" variant="secondary" onClick={onAgain}>
          Import another file
        </PillButton>
        <Link className="font-semibold text-[13px] text-brand" to="/contacts">
          See your contacts
        </Link>
      </div>
    </div>
  )
}

function Figure({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="rounded-card border border-line bg-surface-2 p-4">
      <div className="font-semibold text-[12px] text-t2">{label}</div>
      <div className="mt-1 font-bold font-num text-[22px] text-t1 tabular-nums">{count(value)}</div>
    </div>
  )
}
